import { useState } from "react";
import { Wallet, Loader2, Building2, Filter, FilterX } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, OPERATORS, rupiah } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/Auth";
import { SimFilterDialog } from "@/components/SimFilterDialog";
import { PtBadge, colorMap } from "@/components/PtBadge";

const OP_CHIPS = [["indosat", "isat"], ["telkomsel", "tsel"], ["smartfren", "sfren"], ["xl", "xl"], ["tri", "tri"]];
const chip = (active) => `shrink-0 inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors duration-150 ${active ? "bg-emerald-500 text-white border-emerald-500 font-semibold" : "border-slate-300 text-slate-700 hover:bg-slate-100"}`;

function SimRow({ n, selected, onChecked, colors }) {
  const { can } = useAuth();
  const [busy, setBusy] = useState(false);
  const op = OPERATORS[n.operator];
  const check = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/slots/${n.device_id}/${n.port}/${n.slot}/check-balance`);
      toast.success(`${n.name}: ${r.data.balance != null ? rupiah(r.data.balance) : "saldo tidak terbaca"}`);
      onChecked(n.key, r.data);
    } catch (err) {
      toast.error(`${n.name}: ${errMsg(err)}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid={`sim-name-item-${n.name}${n.company ? `-${n.company}` : ""}`} className={`grid grid-cols-[34px_1fr_auto] items-center gap-2 rounded px-2 py-1.5 ${selected ? "bg-emerald-500/[0.07]" : "hover:bg-slate-50"}`}>
      {!can("check_balance") ? <span /> : <button data-testid={`sim-check-balance-${n.name}${n.company ? `-${n.company}` : ""}`} onClick={check} disabled={busy} title="Cek pulsa"
        className="h-8 w-8 grid place-items-center rounded border border-slate-300 text-slate-700 hover:border-emerald-500 hover:text-emerald-700 transition-colors duration-150 disabled:opacity-50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" />}
      </button>}
      <span className="min-w-0">
        <span className="flex items-center gap-1.5">
          <span className="font-bold text-emerald-700 truncate">{n.name}</span>
          <PtBadge name={n.company} colors={colors} />
          {n.unread > 0 && <span className="rounded-full bg-emerald-500 text-white text-[10px] font-bold px-1.5">{n.unread}</span>}
        </span>
        <span className="block text-[10px] font-mono text-slate-500 truncate">{n.device_name} · {n.port}.{n.slot} <span className={op?.color}>{op?.short}</span> ({n.total})</span>
      </span>
      <span className="text-right leading-tight">
        <span data-testid={`sim-balance-${n.name}${n.company ? `-${n.company}` : ""}`} className="block text-[11px] font-mono text-slate-800 whitespace-nowrap">{n.balance == null ? "—" : rupiah(n.balance)}</span>
        <TopupBar n={n} />
      </span>
    </div>
  );
}

const short = (v) => (v >= 1e6 ? `${+(v / 1e6).toFixed(2)}jt` : v >= 1e3 ? `${Math.round(v / 1e3)}rb` : `${v}`);

function TopupBar({ n }) {
  const lim = n.topup_limit || 0;
  if (!lim) return null;
  const v = n.topup_today || 0;
  const pct = Math.min(100, (v / lim) * 100);
  const lvl = v >= lim ? "full" : pct >= 80 ? "near" : "ok";
  const cls = { full: "text-red-600 font-bold", near: "text-amber-600 font-semibold", ok: "text-slate-500" }[lvl];
  const bar = { full: "bg-red-500", near: "bg-amber-400", ok: "bg-emerald-500" }[lvl];
  return (
    <span data-testid={`sim-topup-${n.name}${n.company ? `-${n.company}` : ""}`} data-level={lvl} className="block mt-0.5" title={`Pulsa masuk hari ini ${rupiah(v)} dari limit ${rupiah(lim)} (reset 00:00 WIB)`}>
      <span className={`block text-[10px] font-mono whitespace-nowrap ${cls}`}>{lvl === "full" ? "LIMIT " : ""}{short(v)}/{short(lim)}</span>
      <span className="block h-1 w-full rounded bg-slate-200 overflow-hidden"><span className={`block h-full ${bar}`} style={{ width: `${pct}%` }} /></span>
    </span>
  );
}

export function SimSidebar({ names, setNames, selected, setSelected, company, setCompany, companies, ops, setOps, search, setSearch, onlyOn, toggleOn }) {
  const [open, setOpen] = useState(false);
  const colors = colorMap(companies);
  const scoped = names.filter((n) => (!company || n.company === company) && (!ops.length || ops.includes(n.operator)));
  const q = search.toLowerCase();
  const isOn = (n) => n.current && n.status === "active";
  const shown = (selected.length ? scoped.filter((n) => selected.includes(n.key)) : onlyOn ? scoped.filter(isOn) : scoped)
    .filter((n) => n.name.toLowerCase().includes(q) || (n.company || "").toLowerCase().includes(q));
  const onChecked = (key, r) => setNames((all) => all.map((n) => (n.key === key ? { ...n, balance: r.balance ?? n.balance } : n)));
  const toggleOp = (k) => setOps(ops.includes(k) ? ops.filter((x) => x !== k) : [...ops, k]);
  const scope = [company || "Semua PT", ops.length ? ops.map((k) => OP_CHIPS.find((o) => o[0] === k)?.[1]).join(", ") : "semua operator"].join(" · ");
  const active = selected.length || company || ops.length || search;
  const clearAll = () => { setSelected([]); setCompany(""); setOps([]); setSearch(""); };
  const { can } = useAuth();
  const [bulkBusy, setBulkBusy] = useState(false);
  const onShown = shown.filter(isOn);
  const checkAll = async () => {
    if (!window.confirm(`Cek saldo ${onShown.length} SIM yang sedang ON (${scope})?`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/check-balance/bulk", { keys: onShown.map((n) => n.key) });
      toast.success(`Cek saldo ${r.data.started} SIM dimulai. Saldo muncul bertahap.`);
      setTimeout(() => setBulkBusy(false), 15000);
    } catch (e) {
      toast.error(errMsg(e));
      setBulkBusy(false);
    }
  };
  return (
    <aside className="lg:w-[25rem] shrink-0 rounded-lg border border-slate-200 bg-white flex flex-col max-h-[60vh] lg:max-h-[calc(100vh-8rem)]">
      <div className="p-3 border-b border-slate-200 space-y-2.5">
        {companies.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="company-chips">
            <button data-testid="company-chip-all" className={chip(!company)} onClick={() => setCompany("")}>Semua PT</button>
            {companies.map((c) => (
              <button key={c.name} data-testid={`company-chip-${c.name}`} className={chip(company === c.name)} onClick={() => setCompany(company === c.name ? "" : c.name)}>
                <Building2 className="h-3 w-3" />{c.name}
              </button>
            ))}
          </div>
        )}
        <Input data-testid="sim-name-search" placeholder="Cari nama SIM…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 bg-white" />
        <div className="flex flex-wrap gap-1.5" data-testid="operator-chips">
          {OP_CHIPS.map(([k, label]) => (
            <button key={k} data-testid={`operator-chip-${label}`} className={chip(ops.includes(k))} onClick={() => toggleOp(k)}>{label}</button>
          ))}
        </div>
        <div className="flex items-center justify-between pt-0.5">
          <button data-testid="sim-filter-open" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 text-sm text-emerald-700 hover:text-emerald-700">
            <Filter className="h-4 w-4" />Filter{selected.length ? ` (${selected.length})` : ""}
          </button>
          <button data-testid="sim-filter-clear" onClick={clearAll} disabled={!active} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-red-600 disabled:opacity-40">
            <FilterX className="h-4 w-4" />Clear filter
          </button>
        </div>
      </div>
      {can("check_balance_all") && (
        <div className="px-3 pt-2">
          <button data-testid="check-all-balance" onClick={checkAll} disabled={bulkBusy || !onShown.length}
            className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-emerald-500 text-emerald-700 text-sm font-semibold py-1.5 hover:bg-emerald-50 disabled:opacity-40">
            {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" />}Cek semua saldo ({onShown.length} ON)
          </button>
        </div>
      )}
      <div className="flex items-center justify-between px-3 pt-2 text-xs text-slate-500">
        <span data-testid="sim-list-caption">{selected.length ? `Dipilih lewat Filter (${shown.length})` : onlyOn ? `Sedang ON (${shown.length})` : `Semua SIM (${shown.length})`}</span>
        {!selected.length && (
          <button data-testid="sim-only-on-toggle" onClick={toggleOn} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-2 py-0.5 hover:bg-slate-50">
            <span className={`h-2 w-2 rounded-full ${onlyOn ? "bg-green-500" : "bg-slate-300"}`} />{onlyOn ? "Hanya ON" : "Tampilkan semua"}
          </button>
        )}
      </div>
      <div className="overflow-y-auto p-2 space-y-0.5 flex-1">
        {shown.map((n) => <SimRow key={n.key} n={n} selected={selected.includes(n.key)} onChecked={onChecked} colors={colors} />)}
        {!shown.length && <p className="text-xs text-slate-500 p-3">{onlyOn && !selected.length ? "Tidak ada SIM bernama yang sedang ON. Pakai Filter untuk melihat SMS lama." : "Tidak ada SIM bernama yang cocok."}</p>}
      </div>
      <SimFilterDialog open={open} onClose={() => setOpen(false)} items={scoped} selected={selected} onApply={setSelected} scope={scope} colors={colors} />
    </aside>
  );
}
